<template>
  <!-- Height fixed to whole pixels: its buttons' 22.4px line height made it 83.59px, and the
       page below sat on a fraction of a pixel, which blurred text and icons without retina -->
  <div
    class="top-dash flex items-center justify-between px-4 md:px-6 py-4 md:h-[84px] bg-grey-light sticky top-16 md:top-0 z-10"
  >
    <!-- Left: Title Section -->
    <div class="title-section flex items-center gap-3 min-w-32">
      <Button
        v-if="showBack"
        icon="pi pi-arrow-left"
        severity="secondary"
        outlined
        :label="backLabel"
        @click="emit('back')"
      />
      <slot name="title">
        <h1 class="text-2xl font-bold text-gray-800">{{ defaultTitle }}</h1>
      </slot>
    </div>

    <!-- Middle: Search Section -->
    <div v-if="showSearch" class="search-section flex-1 max-w-2xl mx-8">
      <slot name="search">
        <IconField iconPosition="left">
          <InputIcon :class="isSearching ? 'pi pi-spin pi-spinner' : 'pi pi-search'" />
          <InputText
            v-model="searchQuery"
            :placeholder="searchPlaceholder"
            class="w-full"
            @input="handleSearch"
          />
        </IconField>
      </slot>
    </div>

    <!-- Right: User Dropdown -->
    <div class="user-section flex items-center gap-4">
      <slot name="actions">
        <!-- Optional slot for additional actions (notifications, etc.) -->
      </slot>

      <!-- Renders itself only for users assigned to more than one customer. -->
      <LCustomerSwitcher />

      <!-- Renders itself only on dev / QA, where the Work4All tenant can be switched -->
      <LTenantSwitcher />

      <Button
        :label="getUsername()"
        icon="pi pi-user"
        severity="secondary"
        text
        rounded
        aria-label="Profile"
        @click="toggleUserMenu"
        aria-haspopup="true"
        :aria-expanded="userMenuVisible"
      >
      </Button>

      <Menu ref="userMenu" :model="userMenuItems" :popup="true">
        <template #item="{ item, props }">
          <a class="flex items-center" v-bind="props.action">
            <span :class="item.icon" class="mr-2" />
            <span>{{ item.label }}</span>
          </a>
        </template>
      </Menu>
    </div>

    <!-- Change password: sends the reset mail to the user's own address (MBA-F-039) -->
    <Dialog
      v-model:visible="changePasswordVisible"
      modal
      :header="t('auth.changePassword.title')"
      :closable="!changePasswordSending"
      class="w-[92vw] max-w-md"
    >
      <p class="text-sm text-grey-600">
        {{
          changePasswordSent
            ? t('auth.changePassword.sent', { email: userEmail })
            : t('auth.changePassword.confirm', { email: userEmail })
        }}
      </p>

      <template #footer>
        <Button
          v-if="changePasswordSent"
          :label="t('auth.changePassword.close')"
          @click="changePasswordVisible = false"
        />
        <template v-else>
          <Button
            :label="t('auth.changePassword.cancel')"
            severity="secondary"
            text
            :disabled="changePasswordSending"
            @click="changePasswordVisible = false"
          />
          <Button
            :label="t('auth.changePassword.send')"
            :loading="changePasswordSending"
            :disabled="!userEmail"
            @click="sendChangePasswordLink"
          />
        </template>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import LCustomerSwitcher from '@/components/global/LCustomerSwitcher.vue'
import LTenantSwitcher from '@/components/global/LTenantSwitcher.vue'
import AuthService from '@/services/authServices'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()
const { t } = useI18n()

// Props
interface Props {
  defaultTitle?: string
  searchPlaceholder?: string
  showSearch?: boolean
  showBack?: boolean
  backLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  defaultTitle: 'Dashboard',
  searchPlaceholder: 'Search...',
  showSearch: false,
  showBack: false,
  backLabel: 'Back',
})

// Emits
const emit = defineEmits<{
  search: [query: string]
  back: []
}>()

// State
const searchQuery = ref('')
const userMenuVisible = ref(false)
const userMenu = ref()
const isSearching = ref(false)

// User menu items. An Admin Hub session is LA CONCEPT acting as the customer,
// so it gets no password change: the mail would go to the customer.
const userMenuItems = computed(() => [
  ...(authStore.isAdminHubSession
    ? []
    : [
        {
          label: t('auth.changePassword.menu'),
          icon: 'pi pi-key',
          command: openChangePassword,
        },
      ]),
  {
    label: t('nav.logout'),
    icon: 'pi pi-sign-out',
    command: () => {
      authStore.logout()
    },
  },
])

// ── Change password ──
// There is no in-app password change: the user gets the same reset mail as
// "Passwort vergessen", sent to their own address, and stays logged in.
// A failure is shown by the global error toast; the dialog stays open to retry.
const changePasswordVisible = ref(false)
const changePasswordSending = ref(false)
const changePasswordSent = ref(false)
const userEmail = computed(() => authStore.user?.email ?? '')

function openChangePassword() {
  changePasswordSent.value = false
  changePasswordVisible.value = true
}

async function sendChangePasswordLink() {
  if (!userEmail.value) return
  changePasswordSending.value = true
  try {
    await AuthService.sendForgotPasswordLink({ email: userEmail.value })
    changePasswordSent.value = true
  } catch {
    // The global error toast reports it.
  } finally {
    changePasswordSending.value = false
  }
}

// Methods
const handleSearch = () => {
  isSearching.value = true
  emit('search', searchQuery.value)
  setTimeout(() => {
    isSearching.value = false
  }, 3000)
}

const toggleUserMenu = (event: Event) => {
  userMenu.value.toggle(event)
}

function getUsername() {
  const user = authStore.user
  // Admin-hub customer logins don't carry a normal user shape, so fall back to
  // the tracked customer name and guard every field.
  const nameParts = [user?.first_name, user?.middle_name, user?.last_name].filter(Boolean)
  const label = nameParts.length > 0 ? nameParts.join(' ') : user?.email
  const customerName = user?.customer?.name ?? authStore.adminHubCustomerName
  // Multi-customer users get the company from LCustomerSwitcher next to this
  // button, so repeating it here would only make the header noisier.
  if (authStore.canSwitchCustomer) return label || ''
  if (label && customerName) return `${label} - ${customerName}`
  return label || customerName || ''
}
</script>

<style scoped></style>
